import { useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Card, CardBody } from '@/shared/components/Card';
import { LoadingSpinner } from '@/shared/components/Loading';
import MeditationRoom, { ROOM_PRACTICE_LABELS } from '@/models/meditation-room';
import { useMeditationRooms } from '@/services/meditation-rooms';
import { addButtonStyle, darkBorder, darkPanelStyle, darkSuccessText, darkText, darkTextDim, plainButtonStyle } from '@/shared/dark-inline-styles';
import RoomModal from './components/room-modal';

/**
 * Meditate Together — the rooms members enter to practise.
 *
 * A handful of curated records, so this is a list rather than the sorted,
 * paginated table the bigger sections use: pagination over six rows is furniture.
 *
 * NOT live sessions. Those are coach-led calls with media, scheduled in the Live
 * Sessions section, and the two must not be confused — a room here has no host,
 * no participants and no call.
 */
const MeditationRooms = () => {
    const { data, isLoading } = useMeditationRooms();
    const [editing, setEditing] = useState<MeditationRoom | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const rooms = [...(data?.data?.rooms ?? [])].sort((a, b) => a.orderIndex - b.orderIndex || a.name.localeCompare(b.name));

    const open = (room: MeditationRoom | null) => {
        setEditing(room);
        setIsOpen(true);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title" style={{ color: darkText }}>
                        Meditation Rooms
                    </h3>
                    <p style={{ color: darkTextDim, marginTop: -8, marginBottom: 20, fontSize: 13, maxWidth: 640 }}>
                        One room per practice, shown on the app&apos;s Meditate Together tab. Members enter a room and sit; their rank comes from
                        sitting, so there is nothing to award here.
                    </p>
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <Card>
                        <CardBody>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                                <button type="button" style={addButtonStyle} onClick={() => open(null)}>
                                    Add room
                                </button>
                            </div>

                            {isLoading ? (
                                <LoadingSpinner />
                            ) : rooms.length === 0 ? (
                                <p style={{ color: darkTextDim, padding: '32px 0', textAlign: 'center' }}>
                                    No rooms yet. The app shows an empty Rooms tab until one exists.
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {rooms.map((room) => (
                                        <div
                                            key={room.id}
                                            style={{ ...darkPanelStyle, display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px' }}
                                        >
                                            <span
                                                style={{
                                                    fontFamily: 'monospace',
                                                    fontSize: 11,
                                                    color: darkTextDim,
                                                    border: `1px solid ${darkBorder}`,
                                                    borderRadius: 4,
                                                    padding: '2px 6px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                title="Permanent — every recorded sit points at this"
                                            >
                                                {room.key}
                                            </span>

                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ color: darkText, fontWeight: 600 }}>{room.name}</div>
                                                <div style={{ color: darkTextDim, fontSize: 12 }}>
                                                    {ROOM_PRACTICE_LABELS[room.practice] ?? room.practice}
                                                    {room.tagline ? ` · ${room.tagline}` : ''}
                                                </div>
                                            </div>

                                            <span style={{ fontSize: 12, color: room.active ? darkSuccessText : darkTextDim, whiteSpace: 'nowrap' }}>
                                                {room.active ? 'Live' : 'Hidden'}
                                            </span>

                                            <button type="button" style={plainButtonStyle} onClick={() => open(room)}>
                                                Edit
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {isOpen && <RoomModal room={editing} onClose={() => setIsOpen(false)} />}
        </Container>
    );
};

export default MeditationRooms;
